import type { FC } from "react";
import { useEffect, useRef, useState } from "react";
import { identity, isEqual } from "lodash";
import type {
    IAutocompleteKeyword,
    IAutocompleteKeywordGroup,
} from "pro-features/components/AutocompleteKeywords/types/AutocompleteKeywordGroupTypes";
import type { IRule, MultiIncludeExcludeKeywordsProps } from "./types";
import { createNewRow, MultiIncludeExcludeKeywordsTextField } from "./utils";
import { MultiIncludeExcludePopupContent } from "./MultiIncludeExcludePopupContent";
import { allTrackers } from "app/services/track/track";

export const MultiIncludeExcludeKeywordsInline: FC<Omit<MultiIncludeExcludeKeywordsProps, "getButtonText">> = ({
    rules: initialRules,
    onMultiIncludeExcludeChange,
    maxRules = 3,
    maxKeywordsPerRule = 20,
    withMatchType = true,
}) => {
    const contentContainerRef = useRef(null);
    const [rules, setRules] = useState<IRule[]>(initialRules ?? [createNewRow()]);

    useEffect(() => {
        if (!rules.length) {
            setRules([createNewRow()]);
        } else if (!isEqual(initialRules, rules)) {
            const rulesToNotifyOn = rules.filter((rule) => rule.items.length);
            onMultiIncludeExcludeChange(rulesToNotifyOn);
        }
    }, [rules]);

    const addRule = () => {
        allTrackers.dispatchTrackEvent("Drop Down", "click", "Add rule");
        setRules([...rules, createNewRow()]);
        queueMicrotask(() => {
            contentContainerRef.current.scrollTop = contentContainerRef.current.scrollHeight;
        });
    };

    const deleteRule = (index: number) => {
        setRules([...rules.slice(0, index), ...rules.slice(index + 1)]);
    };

    const onRowTypeChange = (index: number, rule: Partial<Pick<IRule, "matchType" | "type">>) => {
        setRules([...rules.slice(0, index), createNewRow({ ...rules[index], ...rule }), ...rules.slice(index + 1)]);
    };

    const onKeywordSelected = (item: string | IAutocompleteKeyword | IAutocompleteKeywordGroup, index: number) => {
        const trackKeywordAdd = (keyword: string, isGroup = false) => {
            allTrackers.dispatchTrackEvent(
                "Drop Down",
                "click",
                `keyword added/${keyword || ""}/${isGroup ? "group" : "keyword"}`,
            );
        };
        const handleKeyword = (keyword: string) => {
            const commaSeparatedItems = keyword
                .split(",")
                .map((item) => item.trim())
                .filter(identity);
            const uniqueCommaSeparatedItems = Array.from(new Set(commaSeparatedItems));
            trackKeywordAdd(uniqueCommaSeparatedItems.join(","));

            if (uniqueCommaSeparatedItems.length > 1) {
                setRules((currentRules) => {
                    currentRules[index].items.push(
                        ...uniqueCommaSeparatedItems.map((value) => ({ type: "keyword", value }) as const),
                    );
                    currentRules[index].items = currentRules[index].items.slice(0, maxKeywordsPerRule);
                    return [...currentRules];
                });
            } else if (keyword.trim().length) {
                setRules((currentRules) => {
                    currentRules[index].items.push({ type: "keyword", value: keyword });
                    return [...currentRules];
                });
            }
        };
        if (typeof item === "string") {
            handleKeyword(item);
        } else if (item.hasOwnProperty("name")) {
            handleKeyword((item as IAutocompleteKeyword).name);
        } else {
            trackKeywordAdd((item as IAutocompleteKeywordGroup).Name, true);
            setRules((currentRules) => {
                currentRules[index].items.push({
                    type: "keywordList",
                    value: (item as IAutocompleteKeywordGroup).Id,
                });
                return [...currentRules];
            });
        }
    };

    const onRemoveKeyword = (keyword: string, type: "keyword" | "keywordList", _, index: number) => () => {
        setRules((currentRules) => {
            currentRules[index].items = currentRules[index].items.filter(
                (item) => item.type === type && item.value !== keyword,
            );
            return [...currentRules];
        });
    };

    return (
        <MultiIncludeExcludePopupContent
            fieldType="keywords"
            rules={rules}
            maxItemsPerRule={maxKeywordsPerRule}
            addRow={addRule}
            deleteRow={deleteRule}
            onRowTypeChange={onRowTypeChange}
            onItemSelected={onKeywordSelected}
            onRemoveItem={onRemoveKeyword}
            maxRules={maxRules}
            setContainerRef={(ref) => (contentContainerRef.current = ref)}
            withHeader={false}
            withFooter={false}
            withMatchType={withMatchType}
        >
            {MultiIncludeExcludeKeywordsTextField}
        </MultiIncludeExcludePopupContent>
    );
};
